import { AxleMenu, MenuItemInfo } from '@/components/AxleMenu';
import { AxleIconButton } from '@/components/buttons/AxleIconButton';
import { CancelVisitDialog } from '@/components/cancel/CancelVisit';
import { HorizontalStack } from '@/components/utils/linearLayouts';
import {
    CanRescheduleOrCancelVisitQuery,
    CanRescheduleOrCancelVisitQueryVariables,
} from '@/components/visits/tables/VisitActionsCell.gql.types';
import { WarningsIcon } from '@/components/warnings/WarningsIcon';
import { DialogType, useDialog } from '@/dialogs/DialogManager';
import { useAppConfig } from '@/fetching/api/appConfig';
import {
    REASSIGN_DISABLED_NO_SERVICES_TOOLTIP,
    useCanCancelVisit,
    useCanReassignVisit,
    useCanRescheduleVisit,
    visitHasServices,
} from '@/hooks/permissions/visit';
import { errorToast, successToast } from '@/toast';
import { VisitOutcome, VisitState, isVisitComplete } from '@/types/visit';
import { useBooleanState } from '@/utils/booleanState';
import { useOpenLinkWithNewTabSupport } from '@/utils/links';
import { gql, useMutation, useQuery } from '@apollo/client';
import { MoreVert } from '@mui/icons-material';
import { FC, type MouseEvent, useCallback, useMemo, useRef } from 'react';
import { useHistory } from 'react-router-dom';

export const CanRescheduleOrCancelVisitFragment = gql`
    fragment CanRescheduleOrCancelVisitFragment on Visit {
        id
        state
        outcome
        patient {
            id
        }
        permissions {
            id
            reschedule
            assign
            cancel
            editWithCompletedOutcome
        }
        clinicianAppointments {
            id
            services {
                id
            }
        }
    }
`;

const CAN_RESCHEDULE_OR_CANCEL_VISIT_QUERY = gql`
    query CanRescheduleOrCancelVisit($visitId: UUID!) {
        visit(id: $visitId) {
            ...CanRescheduleOrCancelVisitFragment
            requiresReschedule
            warnings {
                message
            }
        }
    }
`;

export const VisitActionsCellFragment = gql`
    fragment VisitActionsCellFragment on Visit {
        id
        visitState: state
        outcome
        requiresReschedule
        order {
            id
        }
        warnings {
            message
        }
    }
`;

const REVIEW_MISSED_VISIT_MUTATION = gql`
    mutation ReviewMissedOrderVisit($visitId: UUID!, $requiresReschedule: Boolean!) {
        markVisitAsMissed(input: { visitId: $visitId, requiresReschedule: $requiresReschedule }) {
            id
            state
            outcome
            requiresReschedule
            order {
                ...OrderStatusFragment
            }
        }
    }
`;

export interface Visit {
    id: string;
    visitState: VisitState;
    outcome: VisitOutcome;
    requiresReschedule: boolean | null;
    order: {
        id: string;
    } | null;
    warnings: { message: string }[];
}

type VisitActionsCellProps = {
    visitId: string;
};

export const VisitActionsCell: FC<VisitActionsCellProps> = (props) => {
    const { visitId } = props;
    const openLinkWithNewTabSupport = useOpenLinkWithNewTabSupport();
    const history = useHistory();
    const appConfig = useAppConfig();
    const [isMenuOpen, openMenu, closeMenu] = useBooleanState(false);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const [isCancelDialogOpen, onOpenCancelDialog, onCloseCancelDialog] = useBooleanState(false);
    const [reviewMissedVisit] = useMutation(REVIEW_MISSED_VISIT_MUTATION);
    const { data } = useQuery<
        CanRescheduleOrCancelVisitQuery,
        CanRescheduleOrCancelVisitQueryVariables
    >(CAN_RESCHEDULE_OR_CANCEL_VISIT_QUERY, {
        variables: {
            visitId,
        },
    });
    const { openDialog } = useDialog();
    const canReassignVisit = useCanReassignVisit(data?.visit);
    const hasServices = visitHasServices(data?.visit);
    const canRescheduleVisit = useCanRescheduleVisit(data?.visit);
    const canCancelVisit = useCanCancelVisit(data?.visit ?? null);

    // Edit permission: can edit if the dialog is enabled and the visit is editable
    // (not complete OR has editWithCompletedOutcome permission)
    const canEditVisit =
        appConfig.visit.edit.dialog &&
        data?.visit &&
        (!isVisitComplete(data.visit.state) || data.visit.permissions.editWithCompletedOutcome);

    const onChangeVisit = useCallback(
        (event: MouseEvent<HTMLElement>) => {
            closeMenu();
            openLinkWithNewTabSupport(`/visits/${visitId}/schedule`, event);
        },
        [closeMenu, openLinkWithNewTabSupport, visitId],
    );

    const onReassignVisit = useCallback(() => {
        closeMenu();
        openDialog(DialogType.ReassignVisits, {
            visitsToReassign: [visitId],
            visitRequestsToReassign: [],
            cliniciansToReassign: [],
        });
    }, [closeMenu, openDialog, visitId]);

    const onRescheduleVisit = useCallback(() => {
        reviewMissedVisit({
            variables: {
                visitId: visitId,
                requiresReschedule: data?.visit?.requiresReschedule,
            },
        })
            .then(() => {
                successToast('Unable to reschedule visit.');
            })
            .catch(() => {
                errorToast('Unable to reschedule visit.');
            });
    }, [reviewMissedVisit, visitId, data?.visit?.requiresReschedule]);

    const onCancelVisit = useCallback(() => {
        closeMenu();
        onOpenCancelDialog();
    }, [closeMenu, onOpenCancelDialog]);

    const onEditVisit = useCallback(() => {
        closeMenu();
        openDialog(DialogType.EditVisit, { visitId: visitId });
    }, [closeMenu, openDialog, visitId]);

    const items = useMemo(() => {
        const menuItems: MenuItemInfo[] = [];
        if (canRescheduleVisit) {
            if (data?.visit?.requiresReschedule) {
                menuItems.push({
                    label: 'Requires Reschedule',
                    onClick: onRescheduleVisit,
                });
            } else {
                switch (data?.visit?.state) {
                    case VisitState.CONFIRMED:
                        menuItems.push({
                            label: 'Reschedule',
                            onClick: onChangeVisit,
                        });
                        break;
                    default:
                        menuItems.push({
                            label: 'Book',
                            onClick: onChangeVisit,
                        });
                        break;
                }
            }
        }
        if (canReassignVisit) {
            menuItems.push({
                label: 'Reassign',
                onClick: onReassignVisit,
                disabled: !hasServices,
                tooltip: !hasServices ? REASSIGN_DISABLED_NO_SERVICES_TOOLTIP : undefined,
            });
        }
        if (canEditVisit) {
            menuItems.push({
                label: 'Edit',
                onClick: onEditVisit,
            });
        }

        return menuItems.concat([
            {
                condition: canCancelVisit,
                label: 'Cancel',
                onClick: onCancelVisit,
            },
            {
                label: 'View',
                onClick: () => {
                    closeMenu();
                    history.push(`/visits/${visitId}`);
                },
            },
        ]);
    }, [
        canCancelVisit,
        canEditVisit,
        canReassignVisit,
        canRescheduleVisit,
        closeMenu,
        hasServices,
        history,
        onCancelVisit,
        onChangeVisit,
        onEditVisit,
        onReassignVisit,
        onRescheduleVisit,
        visitId,
        data?.visit?.requiresReschedule,
        data?.visit?.state,
    ]);

    return (
        <>
            <AxleMenu
                isOpen={isMenuOpen}
                close={closeMenu}
                anchorEl={buttonRef.current}
                onClick={(event) => {
                    // Prevent all clicks from within these or on the backdrop brought up with this menu
                    // from triggering the table row
                    event.stopPropagation();
                }}
                items={items}
            />
            {data?.visit ? (
                <CancelVisitDialog
                    isOpen={isCancelDialogOpen}
                    onClose={onCloseCancelDialog}
                    visitId={visitId}
                    patientId={data.visit.patient.id}
                />
            ) : null}
            <AxleIconButton
                ref={buttonRef}
                icon={<MoreVert />}
                tooltip={'More actions'}
                onClick={(event) => {
                    // Prevent from propagating to table row click events.
                    event.stopPropagation();
                    openMenu();
                }}
            />
            <HorizontalStack
                onClick={(event) => {
                    // Prevent clicks from propagating to the table row.
                    event.stopPropagation();
                }}
            >
                <WarningsIcon warnings={data?.visit?.warnings} />
            </HorizontalStack>
        </>
    );
};
